import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { ShieldAlert, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const PASSWORD_RULES = [
  { rule: /.{10,}/, label: 'At least 10 characters' },
  { rule: /[a-z]/, label: 'Lowercase letter' },
  { rule: /[A-Z]/, label: 'Uppercase letter' },
  { rule: /\d/, label: 'Digit' },
  { rule: /[!@#$%^&*()\-_=+\[\]{};:,.<>?/|~`]/, label: 'Special character' },
];

const ForcePasswordChangeModal = () => {
  const { user, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const open = !!user && !!user.must_change_password;

  const ruleStatus = PASSWORD_RULES.map((r) => ({ ...r, ok: r.rule.test(newPassword) }));
  const allPassed = ruleStatus.every((r) => r.ok);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!allPassed) {
      setError('Password does not meet the policy requirements');
      return;
    }
    setSubmitting(true);
    const res = await changePassword(currentPassword, newPassword);
    setSubmitting(false);
    if (res.success) {
      toast.success('Password updated. Welcome!');
    } else {
      setError(res.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* enforced: cannot close */ }}>
      <DialogContent
        className="sm:max-w-md [&>button.absolute]:hidden"
        data-testid="force-password-change-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            Password Change Required
          </DialogTitle>
          <DialogDescription>
            For security, you must set a new password before continuing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="cp-current">Current / Temporary password</Label>
            <Input
              id="cp-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="cp-current-input"
              autoComplete="current-password"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              data-testid="cp-new-input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="cp-confirm-input"
              autoComplete="new-password"
            />
          </div>

          <ul className="text-xs space-y-1 bg-muted/50 p-3 rounded-md">
            {ruleStatus.map((r) => (
              <li key={r.label} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                <Check className={`h-3.5 w-3.5 ${r.ok ? 'opacity-100' : 'opacity-30'}`} />
                {r.label}
              </li>
            ))}
          </ul>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={logout} disabled={submitting} data-testid="cp-logout-btn">
              Log out
            </Button>
            <Button type="submit" className="flex-1" disabled={submitting} data-testid="cp-submit-btn">
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChangeModal;
